<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import FaqActionEditor from '@/components/admin/faq/FaqActionEditor.vue'
import {
  useFaqAdminStore,
  type FaqItemAdmin,
  type FaqDetail,
  type FaqStatus,
  type FaqActionInput,
  type CreateFaqPayload,
  type PublishValidationError,
} from '@/stores/useFaqAdminStore'
import { useFeatureRegistryAdminStore } from '@/stores/useFeatureRegistryAdminStore'
import {
  ArchiveBoxIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline'

const store = useFaqAdminStore()
const registryStore = useFeatureRegistryAdminStore()

// ─── Filtres ────────────────────────────────────────────────────────────────
const statusFilter = ref<FaqStatus | ''>('')
const categoryFilter = ref('')
const searchFilter = ref('')

async function applyFilters() {
  await store.fetchAll({
    status: statusFilter.value || undefined,
    category: categoryFilter.value || undefined,
  })
}

const filteredItems = computed(() => {
  if (!searchFilter.value) return store.items
  const q = searchFilter.value.toLowerCase()
  return store.items.filter((i) =>
    (i.questionFr ?? '').toLowerCase().includes(q) ||
    (i.category ?? '').toLowerCase().includes(q) ||
    i.tags.some((t) => t.toLowerCase().includes(q)) ||
    i.featureKeys.some((k) => k.toLowerCase().includes(q)) ||
    i.pageKeys.some((k) => k.toLowerCase().includes(q)),
  )
})

// ─── Édition (création + update) ────────────────────────────────────────────
const showEditModal = ref(false)
const editingDetail = ref<FaqDetail | null>(null) // null en création
const editing = ref(false)
const editError = ref<string | null>(null)

interface EditForm {
  uuid?: string
  status: FaqStatus
  priority: number
  category: string
  tags: string
  featureKeys: string
  pageKeys: string
  questionFr: string
  answerFr: string
  questionEn: string
  answerEn: string
  actions: FaqActionInput[]
}

const form = ref<EditForm>(buildEmptyForm())

function buildEmptyForm(): EditForm {
  return {
    status: 'DRAFT',
    priority: 0,
    category: '',
    tags: '',
    featureKeys: '',
    pageKeys: '',
    questionFr: '',
    answerFr: '',
    questionEn: '',
    answerEn: '',
    actions: [],
  }
}

function detailToForm(d: FaqDetail): EditForm {
  const fr = d.translations.find((t) => t.language === 'fr')
  const en = d.translations.find((t) => t.language === 'en')
  return {
    uuid: d.uuid,
    status: d.status,
    priority: d.priority,
    category: d.category ?? '',
    tags: d.tags.join(', '),
    featureKeys: d.featureKeys.join(', '),
    pageKeys: d.pageKeys.join(', '),
    questionFr: fr?.question ?? '',
    answerFr: fr?.answer ?? '',
    questionEn: en?.question ?? '',
    answerEn: en?.answer ?? '',
    actions: d.actions.map((a) => ({
      uuid: a.uuid,
      order: a.order,
      kind: a.kind,
      labels: { ...a.labels },
      deniedMessages: a.deniedMessages ? { ...a.deniedMessages } : undefined,
      targetPageKey: a.targetPageKey,
      targetCapabilityKey: a.targetCapabilityKey,
      externalUrl: a.externalUrl,
      queryParams: (a.queryParams as Record<string, unknown>) ?? undefined,
    })),
  }
}

function parseCsv(value: string): string[] {
  return value.split(',').map((s) => s.trim()).filter(Boolean)
}

function formToCreatePayload(f: EditForm): CreateFaqPayload {
  const translations = [
    { language: 'fr', question: f.questionFr.trim(), answer: f.answerFr.trim() },
  ]
  if (f.questionEn.trim() || f.answerEn.trim()) {
    translations.push({ language: 'en', question: f.questionEn.trim(), answer: f.answerEn.trim() })
  }
  return {
    status: f.status,
    priority: f.priority,
    featureKeys: parseCsv(f.featureKeys),
    pageKeys: parseCsv(f.pageKeys),
    category: f.category.trim() || null,
    tags: parseCsv(f.tags),
    translations,
    actions: f.actions.length ? f.actions : undefined,
  }
}

function openCreate() {
  editingDetail.value = null
  form.value = buildEmptyForm()
  editError.value = null
  showEditModal.value = true
}

async function openEdit(item: FaqItemAdmin) {
  editingDetail.value = null
  editError.value = null
  showEditModal.value = true
  const detail = await store.fetchDetail(item.uuid)
  if (detail) {
    editingDetail.value = detail
    form.value = detailToForm(detail)
  } else {
    editError.value = 'Impossible de charger le détail'
  }
}

function closeEdit() {
  showEditModal.value = false
  editingDetail.value = null
  editError.value = null
}

async function saveEdit() {
  editing.value = true
  editError.value = null
  try {
    if (!form.value.questionFr.trim() || !form.value.answerFr.trim()) {
      editError.value = 'La question et la réponse FR sont obligatoires.'
      editing.value = false
      return
    }
    const payload = formToCreatePayload(form.value)
    const res = editingDetail.value
      ? await store.update(editingDetail.value.uuid, payload)
      : await store.create(payload)
    if (res.ok) {
      closeEdit()
    } else {
      editError.value = res.message
    }
  } finally {
    editing.value = false
  }
}

// ─── Actions de la FAQ (publish, archive, delete) ───────────────────────────
const publishErrors = ref<PublishValidationError[]>([])
const showPublishErrorsModal = ref(false)

async function publish(item: FaqItemAdmin) {
  const res = await store.publish(item.uuid)
  if (res.ok) {
    if (res.data.ok) {
      // succès
      alert('FAQ publiée ✓')
    } else {
      publishErrors.value = res.data.errors ?? []
      showPublishErrorsModal.value = true
    }
  } else {
    alert(res.message)
  }
}

async function archive(item: FaqItemAdmin) {
  if (!confirm('Archiver cette FAQ ?')) return
  const res = await store.archive(item.uuid)
  if (!res.ok) alert(res.message)
}

async function remove(item: FaqItemAdmin) {
  if (!confirm('Supprimer définitivement cette FAQ ?')) return
  const res = await store.remove(item.uuid)
  if (!res.ok) alert(res.message)
}

// ─── Editeur actions : ajout / suppression / réordonnement ─────────────────
function addAction() {
  form.value.actions.push({
    order: form.value.actions.length,
    kind: 'OPEN_PAGE',
    labels: { fr: '' },
  })
}

function updateAction(idx: number, action: FaqActionInput) {
  form.value.actions[idx] = action
}

function removeAction(idx: number) {
  form.value.actions.splice(idx, 1)
  form.value.actions.forEach((a, i) => (a.order = i))
}

function moveActionUp(idx: number) {
  if (idx === 0) return
  ;[form.value.actions[idx - 1], form.value.actions[idx]] = [form.value.actions[idx], form.value.actions[idx - 1]]
  form.value.actions.forEach((a, i) => (a.order = i))
}

function moveActionDown(idx: number) {
  if (idx >= form.value.actions.length - 1) return
  ;[form.value.actions[idx + 1], form.value.actions[idx]] = [form.value.actions[idx], form.value.actions[idx + 1]]
  form.value.actions.forEach((a, i) => (a.order = i))
}

// ─── Badges ─────────────────────────────────────────────────────────────────
function statusBadgeClass(s: FaqStatus) {
  if (s === 'PUBLISHED') return 'badge-success'
  if (s === 'ARCHIVED') return 'badge-ghost'
  return 'badge-warning'
}

onMounted(async () => {
  await Promise.all([store.fetchAll(), registryStore.fetchFeatures(), registryStore.fetchPages()])
})
</script>

<template>
  <div class="p-6">
    <Bs2Title title="FAQ" subtitle="Référentiel des questions / réponses contextuelles" />

    <div class="flex flex-wrap gap-2 mt-4">
      <router-link to="/dashboard/admin" class="btn btn-outline btn-sm">← Retour dashboard admin</router-link>
      <button class="btn btn-primary btn-sm gap-1" @click="openCreate">
        <PlusIcon class="w-4 h-4" />
        Nouvelle FAQ
      </button>
    </div>

    <!-- Filtres + tableau -->
    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body">
        <div class="flex flex-wrap gap-2 items-center mb-4">
          <select v-model="statusFilter" class="select select-bordered select-sm" @change="applyFilters">
            <option value="">Tous statuts</option>
            <option value="DRAFT">Brouillon</option>
            <option value="PUBLISHED">Publiée</option>
            <option value="ARCHIVED">Archivée</option>
          </select>
          <input
            v-model="categoryFilter"
            type="text"
            placeholder="Catégorie..."
            class="input input-bordered input-sm w-48"
            @blur="applyFilters"
          />
          <input
            v-model="searchFilter"
            type="text"
            placeholder="Rechercher question / tag / feature..."
            class="input input-bordered input-sm w-72"
          />
          <ButtonDaisy text="Actualiser" variant="outline" size="sm" @click="applyFilters" />
        </div>

        <div v-if="store.isLoading" class="py-8 text-center">
          <span class="loading loading-spinner loading-lg" />
        </div>
        <div v-else-if="filteredItems.length === 0" class="py-8 text-center text-base-content/60">
          Aucune FAQ. Créez la première avec le bouton "Nouvelle FAQ".
        </div>
        <div v-else class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Statut</th>
                <th>Question (FR)</th>
                <th>Catégorie</th>
                <th>Rattachement</th>
                <th class="text-center">Actions</th>
                <th>Langues</th>
                <th>Priorité</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredItems" :key="item.uuid" class="hover">
                <td>
                  <span class="badge badge-sm" :class="statusBadgeClass(item.status)">
                    {{ item.status }}
                  </span>
                </td>
                <td class="max-w-md">
                  <div class="text-sm truncate" :title="item.questionFr ?? ''">
                    {{ item.questionFr ?? '(pas de trad FR)' }}
                  </div>
                  <div v-if="item.tags.length" class="flex flex-wrap gap-1 mt-1">
                    <span v-for="t in item.tags" :key="t" class="badge badge-outline badge-xs">{{ t }}</span>
                  </div>
                </td>
                <td>
                  <span v-if="item.category" class="badge badge-ghost badge-sm">{{ item.category }}</span>
                  <span v-else class="text-base-content/40">—</span>
                </td>
                <td class="max-w-xs">
                  <div v-if="item.featureKeys.length" class="text-xs">
                    <span class="text-base-content/60">Features:</span>
                    <span class="font-mono ml-1">{{ item.featureKeys.slice(0, 2).join(', ') }}{{ item.featureKeys.length > 2 ? '...' : '' }}</span>
                  </div>
                  <div v-if="item.pageKeys.length" class="text-xs">
                    <span class="text-base-content/60">Pages:</span>
                    <span class="font-mono ml-1">{{ item.pageKeys.slice(0, 2).join(', ') }}{{ item.pageKeys.length > 2 ? '...' : '' }}</span>
                  </div>
                  <span v-if="!item.featureKeys.length && !item.pageKeys.length" class="text-base-content/40 text-xs">
                    — (global)
                  </span>
                </td>
                <td class="text-center tabular-nums">{{ item.actionsCount }}</td>
                <td>
                  <div class="flex gap-1">
                    <span v-for="l in item.availableLanguages" :key="l" class="badge badge-outline badge-xs uppercase">
                      {{ l }}
                    </span>
                  </div>
                </td>
                <td class="text-center tabular-nums">{{ item.priority }}</td>
                <td>
                  <div class="flex gap-1">
                    <button class="btn btn-ghost btn-xs" @click="openEdit(item)" title="Éditer">
                      <PencilSquareIcon class="w-3 h-3" />
                    </button>
                    <button
                      v-if="item.status === 'DRAFT'"
                      class="btn btn-ghost btn-xs text-success"
                      @click="publish(item)"
                      title="Publier"
                    >
                      <CheckCircleIcon class="w-3 h-3" />
                    </button>
                    <button
                      v-if="item.status === 'PUBLISHED'"
                      class="btn btn-ghost btn-xs"
                      @click="archive(item)"
                      title="Archiver"
                    >
                      <ArchiveBoxIcon class="w-3 h-3" />
                    </button>
                    <button class="btn btn-ghost btn-xs text-error" @click="remove(item)" title="Supprimer">
                      <TrashIcon class="w-3 h-3" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal Création/Édition -->
    <div class="modal" :class="{ 'modal-open': showEditModal }">
      <div class="modal-box max-w-5xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <QuestionMarkCircleIcon class="w-6 h-6" />
          {{ editingDetail ? 'Édition FAQ' : 'Nouvelle FAQ' }}
        </h3>

        <div class="mt-4 space-y-4">
          <!-- Métadonnées -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div class="form-control">
              <label class="label py-1"><span class="label-text text-xs">Statut</span></label>
              <select v-model="form.status" class="select select-bordered select-sm" :disabled="editing">
                <option value="DRAFT">Brouillon</option>
                <option value="PUBLISHED">Publiée</option>
                <option value="ARCHIVED">Archivée</option>
              </select>
            </div>
            <div class="form-control">
              <label class="label py-1"><span class="label-text text-xs">Priorité</span></label>
              <input v-model.number="form.priority" type="number" class="input input-bordered input-sm" :disabled="editing" />
            </div>
            <div class="form-control col-span-2">
              <label class="label py-1"><span class="label-text text-xs">Catégorie éditoriale</span></label>
              <input v-model="form.category" type="text" placeholder="getting-started, troubleshooting..." class="input input-bordered input-sm" :disabled="editing" />
            </div>
          </div>

          <div class="form-control">
            <label class="label py-1">
              <span class="label-text text-xs">Tags (séparés par virgules)</span>
            </label>
            <input v-model="form.tags" type="text" placeholder="facture, pdf, téléchargement" class="input input-bordered input-sm" :disabled="editing" />
          </div>

          <!-- Rattachement -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div class="form-control">
              <label class="label py-1">
                <span class="label-text text-xs">Feature keys (séparées par virgules)</span>
                <span class="label-text-alt text-xs">{{ registryStore.features.length }} disponibles</span>
              </label>
              <input v-model="form.featureKeys" type="text" placeholder="billing.invoices, support" class="input input-bordered input-sm font-mono" :disabled="editing" />
            </div>
            <div class="form-control">
              <label class="label py-1">
                <span class="label-text text-xs">Page keys (séparées par virgules)</span>
                <span class="label-text-alt text-xs">{{ registryStore.pages.length }} disponibles</span>
              </label>
              <input v-model="form.pageKeys" type="text" placeholder="invoices.list" class="input input-bordered input-sm font-mono" :disabled="editing" />
            </div>
          </div>

          <!-- Traduction FR -->
          <div class="border border-base-300 rounded-lg p-3 bg-base-50">
            <h4 class="font-semibold text-sm mb-2">
              Traduction FR <span class="text-error">*</span>
            </h4>
            <div class="space-y-2">
              <div class="form-control">
                <label class="label py-1"><span class="label-text text-xs">Question</span></label>
                <input v-model="form.questionFr" type="text" class="input input-bordered input-sm" :disabled="editing" />
              </div>
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">Réponse (markdown)</span>
                  <span class="label-text-alt text-xs">min 50 chars pour publier</span>
                </label>
                <textarea v-model="form.answerFr" rows="5" class="textarea textarea-bordered textarea-sm font-mono text-xs" :disabled="editing" />
              </div>
            </div>
          </div>

          <!-- Traduction EN (optionnel) -->
          <details class="collapse collapse-arrow bg-base-200">
            <summary class="collapse-title text-sm font-medium">Traduction EN (optionnel)</summary>
            <div class="collapse-content space-y-2">
              <div class="form-control">
                <label class="label py-1"><span class="label-text text-xs">Question EN</span></label>
                <input v-model="form.questionEn" type="text" class="input input-bordered input-sm" :disabled="editing" />
              </div>
              <div class="form-control">
                <label class="label py-1"><span class="label-text text-xs">Réponse EN (markdown)</span></label>
                <textarea v-model="form.answerEn" rows="4" class="textarea textarea-bordered textarea-sm font-mono text-xs" :disabled="editing" />
              </div>
            </div>
          </details>

          <!-- Actions -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-semibold text-sm">Actions ({{ form.actions.length }})</h4>
              <button class="btn btn-ghost btn-xs gap-1" @click="addAction">
                <PlusIcon class="w-3 h-3" />
                Ajouter une action
              </button>
            </div>
            <div v-if="form.actions.length === 0" class="text-xs text-base-content/50 italic py-2">
              Aucune action. Une FAQ peut être purement informative, ou inclure des actions
              (ouvrir une page, déclencher une capability, lien externe).
            </div>
            <div v-else class="space-y-2">
              <FaqActionEditor
                v-for="(action, idx) in form.actions"
                :key="idx"
                :model-value="action"
                :index="idx"
                :is-first="idx === 0"
                :is-last="idx === form.actions.length - 1"
                @update:model-value="updateAction(idx, $event)"
                @remove="removeAction(idx)"
                @move-up="moveActionUp(idx)"
                @move-down="moveActionDown(idx)"
              />
            </div>
          </div>

          <div v-if="editError" class="alert alert-error text-sm">{{ editError }}</div>
        </div>

        <div class="modal-action">
          <button class="btn" @click="closeEdit" :disabled="editing">Annuler</button>
          <button class="btn btn-primary" @click="saveEdit" :disabled="editing">
            {{ editing ? 'Enregistrement…' : 'Enregistrer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeEdit" />
    </div>

    <!-- Modal erreurs de publication -->
    <div class="modal" :class="{ 'modal-open': showPublishErrorsModal }">
      <div class="modal-box max-w-xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <XCircleIcon class="w-6 h-6 text-error" />
          Publication impossible
        </h3>
        <p class="mt-3 text-sm">La FAQ ne respecte pas les règles de publication :</p>
        <ul class="mt-3 space-y-2">
          <li
            v-for="(err, i) in publishErrors"
            :key="i"
            class="alert alert-warning text-sm"
          >
            <ExclamationTriangleIcon class="w-4 h-4 shrink-0" />
            <div>
              <div class="font-mono text-xs opacity-70">{{ err.code }}</div>
              <div>{{ err.message }}</div>
            </div>
          </li>
        </ul>
        <div class="modal-action">
          <button class="btn btn-primary" @click="showPublishErrorsModal = false">Fermer</button>
        </div>
      </div>
      <div class="modal-backdrop" @click="showPublishErrorsModal = false" />
    </div>
  </div>
</template>
